/**
 * /products — Products & Billing. Also served at /upgrade.
 *
 * REDESIGNED, and now led by one hand-authored marketing grid instead of the older
 * entitlement-backed catalogue grid that used to sit below it.
 *
 * WHAT IT IS NOW:
 *   - one two-column grid of comparable offer cards so prices and badges line up
 *   - one card component, shared with onboarding, so styling and owned/buy states stay in sync
 *   - the front-end checkout presented as a real card in the same visual system as the rest
 *   - the same offer data reused by the locked upsell screens, so /upgrade and the locked nav
 *     destinations cannot drift on names or prices
 *
 * Sellable products leave for external JVZoo checkout. Ambiguous marketing cards stay buyable
 * but intentionally do not inherit ownership just because an older catalogue product existed.
 */
import { BackLink } from '../components/ui';
import PlanCard, { ADDON_BUY_DISABLED_REASON } from '../components/account/PlanCard';
import { JVZOO_PRODUCT_URLS } from '../config';
import { SUBSCRIPTION_OFFERS } from '../data/subscriptionOffers';
import { resolveUpgradeOffers } from '../data/upgradeOffers';
import { openExternalUrl } from '../lib/externalNavigation';
import { useAuthStore } from '../stores/authStore';
import { c } from '../styles/theme';
import type { CheckoutProductId } from '../types/domain';
import { paths } from './paths';

export default function Products() {
  const appAccess = useAuthStore((s) => s.appAccess);
  const suspendedAt = useAuthStore((s) => s.user?.suspendedAt);
  const checkoutOwnership = useAuthStore((s) => s.checkoutOwnership);
  const hasConfirmedPurchase = Object.values(checkoutOwnership).some(Boolean);
  const showSubscriptionPlans =
    appAccess === 'purchase_required' && !suspendedAt && !hasConfirmedPurchase;

  const offers = resolveUpgradeOffers({ checkoutOwnership });
  const addonOffers = offers.filter((card) => card.layout === 'addon-inline' && card.visible);
  const mainOffers = offers.filter((card) => card.layout !== 'addon-inline' && card.visible);

  function openCheckout(productId: CheckoutProductId): void {
    const url = JVZOO_PRODUCT_URLS[productId];
    openExternalUrl(url);
  }

  return (
    <div>
      <BackLink fallbackTo={paths.settings} fallbackLabel="Settings" />

      <h1
        style={{
          fontFamily: 'Space Grotesk',
          fontWeight: 600,
          fontSize: 16,
          margin: '0 0 16px',
          color: c.text1,
        }}
      >
        Products &amp; Billing
      </h1>

      {showSubscriptionPlans ? (
        <>
          <h2
            style={{
              fontSize: 12,
              color: c.text2,
              fontWeight: 400,
              fontFamily: 'Inter',
              margin: '0 0 8px',
            }}
          >
            Choose your plan
          </h2>
          <div className="lyx-plan-grid lyx-plan-grid--two-col" style={{ marginBottom: 22 }}>
            {SUBSCRIPTION_OFFERS.map((offer) => (
              <PlanCard
                key={offer.id}
                product={offer.product}
                owned={false}
                recommended={offer.bestValue}
                recommendedLabel="Best value"
                highlightVariant={offer.bestValue ? 'primary' : undefined}
                onBuy={() => {
                  if (offer.checkoutUrl) openExternalUrl(offer.checkoutUrl);
                }}
                buyHref={offer.checkoutUrl}
                checkoutImageUrl={offer.checkoutImageUrl}
                buyLabel="Choose plan"
                buyDisabledReason={offer.checkoutUrl ? undefined : 'Checkout coming soon'}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
            {addonOffers.map((card) => (
              <PlanCard
                key={card.checkoutId}
                product={card.product}
                owned={card.owned}
                recommended={card.recommended}
                recommendedLabel={card.recommendedLabel}
                highlyRecommended={card.highlyRecommended}
                supportingBadgeLabel={card.supportingBadgeLabel}
                highlightVariant={card.highlightVariant}
                layout={card.layout}
                onBuy={() => openCheckout(card.checkoutId)}
                buyHref={JVZOO_PRODUCT_URLS[card.checkoutId]}
                buyDisabledReason={card.owned ? undefined : ADDON_BUY_DISABLED_REASON}
              />
            ))}
          </div>

          <h2
            style={{
              fontSize: 12,
              color: c.text2,
              fontWeight: 400,
              fontFamily: 'Inter',
              margin: '0 0 8px',
            }}
          >
            Add to your account
          </h2>

          <div className="lyx-plan-grid lyx-plan-grid--two-col" style={{ marginBottom: 22 }}>
            {mainOffers.map((card) => (
              <PlanCard
                key={card.checkoutId}
                product={card.product}
                owned={card.owned}
                recommended={card.recommended}
                recommendedLabel={card.recommendedLabel}
                highlyRecommended={card.highlyRecommended}
                supportingBadgeLabel={card.supportingBadgeLabel}
                highlightVariant={card.highlightVariant}
                layout={card.layout}
                onBuy={() => openCheckout(card.checkoutId)}
                buyHref={JVZOO_PRODUCT_URLS[card.checkoutId]}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
